import { Booking, Product } from "../types/booking";

/**
 * Generate a formatted reference number for a booking
 */
export const getBookingReference = (booking: Booking): string => {
  if (booking.reference) return booking.reference;
  const transportAbbrev = booking.transport_type_info?.[0]?.abbreviation || '';
  return `EFF${booking.id}${transportAbbrev}`;
};

/**
 * Generate URL for packing slip
 */
export const generatePackingSlipUrl = (booking: Booking): string | null => {
  if (!booking || !booking.product_info || !booking.company_info || 
      !booking.supplier_info || !booking.booking_delivery_info) {
    return null;
  }

  // Handle different types of data structures safely
  const products = Array.isArray(booking.product_info) && booking.product_info.length > 0
    ? Array.isArray(booking.product_info[0]) 
      ? booking.product_info[0] 
      : booking.product_info
    : [];
    
  const productArray = Array.isArray(products) ? products : [];
    
  const company = Array.isArray(booking.company_info) && booking.company_info.length > 0
    ? booking.company_info[0]
    : booking.company_info;
    
  const supplier = Array.isArray(booking.supplier_info) && booking.supplier_info.length > 0
    ? booking.supplier_info[0]
    : booking.supplier_info;
    
  const deliveryInfo = Array.isArray(booking.booking_delivery_info) && booking.booking_delivery_info.length > 0
    ? booking.booking_delivery_info[0]
    : booking.booking_delivery_info;
    
  const transportTypeInfo = Array.isArray(booking.transport_type_info) && booking.transport_type_info.length > 0
    ? booking.transport_type_info[0]
    : booking.transport_type_info;
    
  const transportType = transportTypeInfo?.abbreviation || '';

  const url = new URL("https://app.efreightforwarding.com/regenerate-packingslip");
  
  // Add query parameters
  const params: Record<string, string> = {
    date: new Date().toLocaleDateString('en-GB'),
    reference: `EFF${booking.id}${transportType}`,
    bookingId: booking.id.toString(),
    companyId: (booking.company_id || '').toString(),
    
    // Ship To information
    shipToCompany: encodeURIComponent(company?.name || ''),
    shipToAddress: encodeURIComponent(company?.address || ''),
    shipToCity: encodeURIComponent(company?.city || ''),
    shipToPostalCode: company?.postal_code || '',
    shipToCountry: company?.country || '',
    shipToEoriNumber: company?.eori_number || '',
    shipToCocNumber: company?.coc_number?.toString() || '',
    shipToStreet: encodeURIComponent(deliveryInfo?.street_name || ''),
    shipToHouseNumber: deliveryInfo?.house_number?.toString() || '',
    
    // Product information
    productIds: productArray.map((p) => p.ean || '').join(','),
    descriptions: productArray.map((p) => encodeURIComponent(p.name || '')).join(','),
    pieces: productArray.map((p) => p.pieces?.toString() || '0').join(','),
    cartons: productArray.map((p) => p.cartons?.toString() || '0').join(','),
    weights: productArray.map((p) => p.gross_weight.toString()).join(','),
    cbms: productArray.map((p) => p.cbm.toString()).join(','),
    netWeights: productArray.map((p) => ((p.gross_weight || 0) * 0.9).toFixed(2)).join(','),
    hsCodes: productArray.map((p) => p.hs_code || '').join(','),
    
    // Supplier information
    supplierInfoId: supplier?.id?.toString() || '',
    supplierInfoName: encodeURIComponent(supplier?.name || ''),
    supplierInfoEmail: encodeURIComponent(supplier?.email || ''),
    supplierInfoPhone: encodeURIComponent(supplier?.phone || ''),
    supplierInfoAddress: encodeURIComponent(supplier?.address || ''),
    supplierInfoCountry: supplier?.country || '',
    supplierInfoProvince: supplier?.province || ''
  };

  // Add all parameters to URL
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });

  return url.toString();
};

/**
 * Get status color based on booking status
 */
export const getStatusColor = (status: string | null): string => {
  if (!status) return "default";
  
  switch (status) {
    case "waiting_for_goods":
      return "blue";
    case "cancelled":
      return "red";
    case "out_for_delivery":
      return "cyan";
    case "concept":
      return "purple";
    case "added_to_stock":
      return "green";
    case "goods_received_origin":
      return "geekblue";
    case "approved":
      return "success";
    case "in_transit":
      return "processing";
    case "custom_clearance":
      return "orange";
    case "archived":
      return "gray";
    case "rejected":
      return "error";
    case "delivered":
      return "green";
    case "submitted":
      return "processing";
    case "in_progress":
      return "warning";
    case "completed":
      return "success";
    default:
      return "default";
  }
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString();
};

/**
 * Format datetime for display
 */
export const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString();
}; 